import { Card, Skeleton, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";

import MDBadgeDot from "@/components/atoms/MDBadgeDot";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import AlertBlock from "@/components/molecules/AlertBlock";
import DefaultLineChart from "@/components/templates/Charts/LineCharts/DefaultLineChart";
import { getDateRange } from "@/helpers/date-helpers";
import useGetSalesPerformance from "@/hooks/requests/dashboard/sales/use-get-sales-performance";
import { SalesDashboardMessages } from "@/messages/salesDashboard/sales-dashboard.messages";
import { DateRange } from "@/types/date.types";

import { translateMonth } from "../../helpers";
import { SalesChartData } from "../../types";

type SalesPerformanceCardProps = {
  messages: SalesDashboardMessages;
};

export default function SalesPerformanceCard({ messages }: SalesPerformanceCardProps) {
  const { startDate, endDate } = getDateRange(DateRange.YEAR);
  const { error, data, isLoading } = useGetSalesPerformance({ startDate, endDate });

  const shouldLoadError = !data && !!error && !isLoading;
  const noData = !data || data.length === 0;

  const [chartData, setChartData] = useState<SalesChartData | null>(null);

  const theme = useTheme();

  // Define the array of color names
  const colorNames = useMemo(
    () =>
      ["primary", "warning", "success", "error", "light", "dark"] as Array<
        "primary" | "warning" | "success" | "error" | "light" | "dark"
      >,
    []
  );

  useEffect(() => {
    if (data && !noData) {
      const labels = data[0].monthlySales.map((sale) => translateMonth(sale.monthName, "pt"));

      const datasets = data.map((channel, index) => {
        const colorName = colorNames[index % colorNames.length];
        const colorValue = theme.palette[colorName]?.main || "#000";

        return {
          label: channel.salesChannel,
          data: channel.monthlySales.map((sale) => sale.totalOrderAmount),
          borderColor: colorValue,
          backgroundColor: colorValue,
          fill: false,
        };
      });

      setChartData({
        labels,
        datasets,
      });
    } else {
      setChartData(null);
    }
  }, [colorNames, data, noData, theme]);

  const renderBadges = () => {
    return data?.map((channel, index) => {
      const colorName = colorNames[index % colorNames.length];
      return (
        <MDBadgeDot key={index} color={colorName} size="sm" badgeContent={channel.salesChannel} />
      );
    });
  };

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={2}>
        <MDTypography variant="h6" fontWeight={"bold"}>
          {messages.monthlySalesEvolutionTitle}
        </MDTypography>
        {isLoading ? (
          <>
            <Skeleton variant="text" width="60%" height={30} />
            <Skeleton variant="rectangular" width="100%" height={"20rem"} sx={{ mt: 2 }} />
            <MDBox display="flex" justifyContent="space-between" mt={2}>
              <Skeleton variant="circular" width={20} height={20} />
              <Skeleton variant="circular" width={20} height={20} />
            </MDBox>
          </>
        ) : !noData && chartData ? (
          <>
            <MDBox display="flex" justifyContent="space-between">
              <MDBox display="flex" ml={-1}>
                {renderBadges()}
              </MDBox>
            </MDBox>
            <DefaultLineChart height="20rem" currency chart={chartData} />
          </>
        ) : shouldLoadError ? (
          <AlertBlock
            title={messages.errorToLoad}
            description={error?.message || ""}
            alt={messages.notFoundAlt}
          />
        ) : (
          <AlertBlock
            title={messages.noDataTitle}
            description={messages.noDataMessage}
            alt={messages.notFoundAlt}
          />
        )}
      </MDBox>
    </Card>
  );
}
